// src/routes/projectManagement.tsx
import React from "react";
import { Route } from "react-router-dom";
import ProtectedRoute from "@/components/ProtectedRoute";
import { EEntity, EModule } from "@/components/table/filter/useTables";

const PageCostCategories = React.lazy(() =>
  import("@/modules/projectManagement/pages/costCategories").then((m) => ({ default: m.PageCostCategories })),
);
const PageDemandOrigins = React.lazy(() =>
  import("@/modules/projectManagement/pages/demandOrigins").then((m) => ({ default: m.PageDemandOrigins })),
);
const PageProjectTypes = React.lazy(() =>
  import("@/modules/projectManagement/pages/projectTypes").then((m) => ({ default: m.PageProjectTypes })),
);
const PageStageTypes = React.lazy(() =>
  import("@/modules/projectManagement/pages/stageTypes").then((m) => ({ default: m.PageStageTypes })),
);
const PageProjectType = React.lazy(() =>
  import("@/modules/projectManagement/pages/projectTypes/id").then((m) => ({ default: m.PageProjectType })),
);
const PageProjects = React.lazy(() =>
  import("@/modules/projectManagement/pages/projects").then((m) => ({ default: m.PageProjects })),
);
const PageProject = React.lazy(() =>
  import("@/modules/projectManagement/pages/projects/id").then((m) => ({ default: m.PageProject })),
);
const PagePortfolioGantt = React.lazy(() =>
  import("@/modules/projectManagement/pages/portfolioGantt").then((m) => ({ default: m.PagePortfolioGantt })),
);

// ----------------------------------------------
// Helper: embrulha no ProtectedRoute (PROJECTMANAGEMENT)
// ----------------------------------------------
type Def = {
  path: string;
  element: React.ReactElement;
  auth: boolean;
  entity: EEntity;
};

const wrap = ({ auth, entity, element }: Omit<Def, "path">) => (
  <React.Suspense fallback={<>Carregando...</>}>
    <ProtectedRoute
      auth={auth}
      {...(auth ? { module: EModule.PROJECTMANAGEMENT, entity } : {})}
    >
      {element}
    </ProtectedRoute>
  </React.Suspense>
);

const costCategories: Def[] = [
  { path: "/projectManagement/cost-categories", auth: true, entity: EEntity.PRJM_COST_CATEGORIES, element: <PageCostCategories /> },
  { path: "/projectManagement/cost-categories/:idCostCategory", auth: true, entity: EEntity.PRJM_COST_CATEGORIES, element: <PageCostCategories /> },
];

const demandOrigins: Def[] = [
  { path: "/projectManagement/demand-origins", auth: true, entity: EEntity.PRJM_DEMAND_ORIGINS, element: <PageDemandOrigins /> },
  { path: "/projectManagement/demand-origins/:idDemandOrigin", auth: true, entity: EEntity.PRJM_DEMAND_ORIGINS, element: <PageDemandOrigins /> },
];

const projectTypes: Def[] = [
  { path: "/projectManagement/project-types", auth: true, entity: EEntity.PRJM_PROJECT_TYPES, element: <PageProjectTypes /> },
  { path: "/projectManagement/project-types/:idProjectType", auth: true, entity: EEntity.PRJM_PROJECT_TYPES, element: <PageProjectType /> },
];

const stageTypes: Def[] = [
  { path: "/projectManagement/stage-types", auth: true, entity: EEntity.PRJM_PROJECT_STAGE_TYPES, element: <PageStageTypes /> },
  { path: "/projectManagement/stage-types/:idStageType", auth: true, entity: EEntity.PRJM_PROJECT_STAGE_TYPES, element: <PageStageTypes /> },
];

const projects: Def[] = [
  { path: "/projectManagement/projects", auth: true, entity: EEntity.PRJM_PROJECTS, element: <PageProjects /> },
  { path: "/projectManagement/projects/:idProject", auth: true, entity: EEntity.PRJM_PROJECTS, element: <PageProject /> },
  { path: "/projectManagement/projects/:idProject/:tab", auth: true, entity: EEntity.PRJM_PROJECTS, element: <PageProject /> },
];

const portfolioGantt: Def[] = [
  { path: "/projectManagement", auth: true, entity: EEntity.PRJM_PROJECTS, element: <PagePortfolioGantt /> },
  { path: "/projectManagement/gantt", auth: true, entity: EEntity.PRJM_PROJECTS, element: <PagePortfolioGantt /> },
];

// ----------------------------------------------
// Exporta as <Route> prontas
// ----------------------------------------------
const defs: Def[] = [
  ...costCategories,
  ...demandOrigins,
  ...projectTypes,
  ...stageTypes,
  ...projects,
  ...portfolioGantt,
];

export const projectManagementRoutes = defs.map(({ path, ...rest }) => (
  <Route key={path} path={path} element={wrap(rest)} />
));
